import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PreviewCard } from '@base-ui/react/preview-card'
import { FileTextIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MessageCitation } from '@hub/contracts'
import { cn } from '@/lib/utils'
import { floating, mono } from '@/components/assistant-ui/elements/surfaces'

/**
 * Inline citation marker — the assistant-ui inline-citation element adapted
 * for project documents: the popover shows the document title and opens it.
 */
export function Citation({
  citation,
  projectId,
}: {
  citation: MessageCitation
  projectId: string
}) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  return (
    <PreviewCard.Root open={open} onOpenChange={setOpen}>
      <PreviewCard.Trigger
        delay={0}
        render={
          <Link to="/$projectId/write/$docId" params={{ projectId, docId: citation.documentId }} />
        }
        className={cn(
          'mx-0.5 inline-flex h-4 min-w-4 translate-y-[-2px] items-center justify-center rounded-[5px] px-1 align-middle font-mono text-[10px] font-medium tabular-nums transition-colors no-underline',
          open
            ? 'bg-foreground text-background'
            : 'bg-foreground/[0.06] text-foreground/45 hover:text-foreground/90'
        )}
      >
        {citation.index}
      </PreviewCard.Trigger>
      <PreviewCard.Portal>
        <PreviewCard.Positioner side="top" sideOffset={8}>
          <PreviewCard.Popup
            className={cn(
              floating,
              'z-50 w-64 origin-(--transform-origin) rounded-2xl p-3.5 outline-none',
              'transition-[opacity,scale] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none',
              'data-[starting-style]:scale-[0.97] data-[starting-style]:opacity-0',
              'data-[ending-style]:scale-[0.97] data-[ending-style]:opacity-0'
            )}
          >
            <div className="flex items-center gap-1.5">
              <span className="bg-foreground/[0.06] text-foreground/45 flex size-4 items-center justify-center rounded">
                <FileTextIcon className="size-2.5" />
              </span>
              <span className={cn(mono, 'text-foreground/40')}>
                {t('citation.source', { index: citation.index })}
              </span>
            </div>
            <p className="mt-2 text-[13px] leading-snug font-medium">{citation.title}</p>
            <Link
              to="/$projectId/write/$docId"
              params={{ projectId, docId: citation.documentId }}
              className="text-foreground/50 hover:text-foreground mt-1 block text-[13px] leading-relaxed underline-offset-2 hover:underline"
            >
              {t('citation.openDocument')}
            </Link>
          </PreviewCard.Popup>
        </PreviewCard.Positioner>
      </PreviewCard.Portal>
    </PreviewCard.Root>
  )
}

/** Compact list of a message's sources (used by the workspace sidebar). */
export function CitationList({
  citations,
  projectId,
}: {
  citations: MessageCitation[]
  projectId: string
}) {
  if (citations.length === 0) return null
  return (
    <ul className="flex flex-col gap-1">
      {citations.map((c) => (
        <li key={c.documentId} className="flex items-center gap-2 text-[13px]">
          <span className={cn(mono, 'text-foreground/35 w-4 text-end tabular-nums')}>
            {c.index}
          </span>
          <Link
            to="/$projectId/write/$docId"
            params={{ projectId, docId: c.documentId }}
            className="text-foreground/80 hover:text-foreground truncate underline-offset-2 hover:underline"
          >
            {c.title}
          </Link>
        </li>
      ))}
    </ul>
  )
}
